/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { FavoriteEntry, FavoritePaging, SiteEntry, SitePaging } from '../model';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
import { buildCollectionParam } from '../../../alfrescoApiClient';
export class FavoritesApi extends BaseApi {
    createFavorite(personId, favoriteBodyCreate, opts) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(favoriteBodyCreate, 'favoriteBodyCreate');
        const pathParams = {
            personId
        };
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/people/{personId}/favorites',
            pathParams,
            queryParams,
            bodyParam: favoriteBodyCreate,
            returnType: FavoriteEntry
        });
    }
    createSiteFavorite(personId, favoriteSiteBodyCreate, opts) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(favoriteSiteBodyCreate, 'favoriteSiteBodyCreate');
        const pathParams = {
            personId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/people/{personId}/favorite-sites',
            pathParams,
            queryParams,
            bodyParam: favoriteSiteBodyCreate
        });
    }
    deleteFavorite(personId, favoriteId) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(favoriteId, 'favoriteId');
        const pathParams = {
            personId,
            favoriteId
        };
        return this.delete({
            path: '/people/{personId}/favorites/{favoriteId}',
            pathParams
        });
    }
    deleteSiteFavorite(personId, siteId) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(siteId, 'siteId');
        const pathParams = {
            personId,
            siteId
        };
        return this.delete({
            path: '/people/{personId}/favorite-sites/{siteId}',
            pathParams
        });
    }
    getFavorite(personId, favoriteId, opts) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(favoriteId, 'favoriteId');
        const pathParams = {
            personId,
            favoriteId
        };
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/people/{personId}/favorites/{favoriteId}',
            pathParams,
            queryParams,
            returnType: FavoriteEntry
        });
    }
    getFavoriteSite(personId, siteId, opts) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(siteId, 'siteId');
        const pathParams = {
            personId,
            siteId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/people/{personId}/favorite-sites/{siteId}',
            pathParams,
            queryParams,
            returnType: SiteEntry
        });
    }
    listFavoriteSitesForPerson(personId, opts) {
        throwIfNotDefined(personId, 'personId');
        const pathParams = {
            personId
        };
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/people/{personId}/favorite-sites',
            pathParams,
            queryParams,
            returnType: SitePaging
        });
    }
    listFavorites(personId, opts) {
        throwIfNotDefined(personId, 'personId');
        opts = opts || {};
        const pathParams = {
            personId
        };
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            orderBy: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.orderBy, 'csv'),
            where: opts === null || opts === void 0 ? void 0 : opts.where,
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/people/{personId}/favorites',
            pathParams,
            queryParams,
            returnType: FavoritePaging
        });
    }
}
